import React, { useState, useEffect, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { CssBaseline, Box, CircularProgress, AppBar, Toolbar, IconButton, Typography, Slide, useScrollTrigger } from "@mui/material";
import { Menu as MenuIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

// Components
import LoginForm from "./components/LoginForm";
import Dashboard from "./components/Dashboard";
import SalesDetail from "./components/SalesDetail";
import PurchaseDetail from "./components/PurchaseDetail";
import About from "./components/About";
import Settings from "./components/Settings";
import Sidebar from "./components/Sidebar";
import GlobalMenuSearch from "./components/GlobalMenuSearch";
import { MenuAccessProvider } from "./components/MenuAccessContext";
import { WebSocketProvider } from "./components/WebSocketContext";
import DownloadPage from "./components/DownloadPage";
import HelpPage from "./components/HelpPage";
import WeighBridge from "./components/WeighBridge";
import HSNSalesDetail from "./components/HSNWiseSalesDetail";
import BranchCreationPage from "./components/BranchCreationPage";
import BranchUpdatePage from "./components/BranchUpdatePage";
import PosAddressConfigPage from "./components/PosAddressConfigPage";
import BranchDayEndSettingsPage from "./components/BranchDayEndSettingsPage";
import DayEndClearPage from "./components/DayEndClearPage";
import UserCreationPage from "./components/UserCreationPage";
import SchemePage from "./components/SchemePage";
import PublishSchemePage from "./components/PublishSchemePage";
import SeasonalReport from "./components/SeasonalReport";
import CategoryTypeMaster from "./components/CategoryTypeMaster";
import CategoryNameMaster from "./components/CategoryNameMaster";
import SupplierCreationForm from "./components/SupplierCreationForm";
import PurchaseEntryForm from "./components/PurchaseEntryForm";
import SalesEntryForm from "./components/SalesEntryForm";
import StockMovementReport from "./components/StockMovementReport";
import ItemMovementReport from "./components/ItemMovementReport";
import ItemVelocityReport from "./components/ItemVelocityReport";
import BillSeriesReport from "./components/BillSeriesReport";
import UploadPage from "./components/UploadPage";
import Invoicedesigner from "./components/InvoiceDesigner";
import CreateItemMaster from "./components/CreateItemMaster";
import SalesSummaryReport from "./components/SalesSummaryReport";
import WorkflowDesignerPage from "./components/WorkflowDesigner/WorkflowDesignerPage";
import RequireWorkflowMenuAccess from "./components/WorkflowDesigner/RequireWorkflowMenuAccess";

import MyTasksPage from "./components/MyTasksPage";
import WorkflowInstancesPage from "./components/WorkflowInstancesPage";
import StockTurnoverReport from "./components/StockTurnoverReport";
import ItemSalesReport from "./components/ItemSalesReport";
import DocumentList from "./components/DocumentList";
import HSNWisePurchaseReport from "./components/HSNWisePurchaseReport";
import HsnSalesSummaryReport from "./components/HsnSalesSummaryReport";
import StockReport from "./components/StockReport";
import POSEntry from "./components/POSEntry";
import ItemSearchPage from "./components/ItemSearchPage";
import MainLayout from "./components/MainLayout";

import StockReportAllBranch from "./components/StockReportAllBranch"
import StockAnomalyReport from "./components/StockAnomalyReport"
import StockReportExclusionPage from "./components/StockReportExclusionPage"

import SalesReportAllBranch from "./components/SalesReportAllBranch"
import BranchSalesSummaryReport from "./components/BranchSalesSummaryReport"
import WeighBridgeEngageReport from "./components/WeighBridgeEngageReport";
import WeighBridgeResync from "./components/WeighBridgeResync";
import WeighbridgeUsageReport from "./components/WeighbridgeUsageReport";
import BranchRequestList from "./components/BranchRequestList";
import PosSessionsPage from "./components/PosSessionsPage";
import ReprocessVoucherForm from "./components/ReprocessVoucherForm"
import BranchStockReport from "./components/BranchStockReport"
import BranchStockDiffReport from "./components/BranchStockDiffReport";
import ItemCategoryLinker from "./components/ItemCategoryLinker";
import TaxUpdateManager from "./components/TaxUpdateManager";
import TaxUpdatePreview from "./components/TaxUpdatePreview";

import SalesCategoryWiseReportAllBranch from "./components/SalesCategoryWiseReportAllBranch";
import StockTransferOutReport from "./components/StockTransferOutReport";
import BranchAssignment from "./components/BranchAssignment";
import BranchTransferAssignment from "./components/BranchTransferAssignment";
import StockTransferInvoicePrint from "./components/StockTransferInvoicePrint";
import StockTransferOutInvoice from "./components/StockTransferOutInvoice";
import CategoryPriceEditDialog from "./components/CategoryPriceEditDialog";
import StockTransferInReport from "./components/StockTransferInReport";
import SalesTaxSummary from "./components/SalesTaxSummary";
import PhysicalStockEntryReport from "./components/PhysicalStockEntryReport";

import FinancialYearPage from "./components/FinancialYearPage";
import DayEndReport from "./components/DayEndReport";
import BranchPricePage from "./components/BranchPricePage";
import PhysicalStockCorrection from "./components/PhysicalStockCorrection";
import WastageEntry from "./components/WastageEntry";
import WastageReport from "./components/WastageReport";
import ProductionDefPage from "./components/ProductionDefPage";
import ProductionPlanningPage from "./components/ProductionPlanningPage";
import ProductionPlanningImport from "./components/ProductionPlanningImport";
import ProductionExecutionPage from "./components/ProductionExecutionPage";
import ProductionPlanningReport from "./components/ProductionPlanningReport";
import ProductionExecutionReport from "./components/ProductionExecutionReport";
import RawMaterialUsageReport from "./components/RawMaterialUsageReport";


import "./i18n"; // i18n config
import LandingPage from "./components/landing/LandingPage";
import PricingPage from "./components/landing/pricing/PricingPage";
import PartnerPage from "./components/landing/PartnerPage";
import PartnerApplyPage from "./components/landing/PartnerApplyPage";
import SignUpPage from "./components/landing/SignUpPage";
import KOTEntry from "./components/KOTEntry";
import GoodsReceiptForm from "./components/GoodsReceiptForm";
import ReceiptModePage from "./components/ReceiptModePage";
import UpiConfigPage from "./components/UpiConfigPage";
import TermsAndConditionsPage from "./components/TermsAndConditionsPage";
import RefundPolicyPage from "./components/RefundPolicyPage";
import PrivacyPolicyPage from "./components/PrivacyPolicyPage";
import BranchStockViewReport from "./components/BranchStockViewReport";
import BranchInventoryReport from "./components/BranchInventoryReport";
import BranchInventoryLedger from "./components/BranchInventoryLedger";
import ItemTransferReport from "./components/ItemTransferReport";
import AIDashboardPage from "./components/AIDashboardPage";
import AIReportChatbot from "./components/AIReportChatbot";
import MenuMasterPage from "./components/MenuMasterPage";
import RoleMenuPage from "./components/RoleMenuPage";
import RoleManagementPage from "./components/RoleManagementPage";
import SalesmanReport from "./components/SalesmanReport";
import CategoryItemReport from "./components/CategoryItemReport";
import CategorySalesSummaryReport from "./components/CategorySalesSummaryReport";
import MyReports from "./components/MyReports";
import RegulatoryIntegration from "./components/regulatory/RegulatoryIntegration";
import LanguagesPage from "./multilanguage/LanguagesPage";
import TallyIntegrationPage from "./tally/TallyIntegrationPage";
import TallySyncStatusPage from "./tally/TallySyncStatusPage";
import { loadPrintPack } from "./multilanguage/printPack";
import TaxAuthorityCallback from "./components/regulatory/TaxAuthorityCallback";
import MenuMapPage from "./components/MenuMapPage";
import SetupWizardPage from "./components/SetupWizardPage";
import StockTransferDiscountPage from "./components/StockTransferDiscountPage";
import PosMachineApprovalPage from "./components/PosMachineApprovalPage";
import BranchProfitReport from "./components/BranchProfitReport";
import ItemCostOverridePage from "./components/ItemCostOverridePage";
import CostPriceHistoryPage from "./components/CostPriceHistoryPage";
import CostStampingPage from "./components/CostStampingPage";
import PurchaseCorrectionPage from "./components/PurchaseCorrectionPage";
import PurchaseCorrectionApprovalPage from "./components/PurchaseCorrectionApprovalPage";
import PurchaseCorrectionHistoryPage from "./components/PurchaseCorrectionHistoryPage";
import BranchExpenseEntryPage from "./components/BranchExpenseEntryPage";
import MonthlyBranchProfitReport from "./components/MonthlyBranchProfitReport";
import ExpenseHeadManagementPage from "./components/ExpenseHeadManagementPage";
import ShopExpenseConsolidatedReport from "./components/ShopExpenseConsolidatedReport";
import FranchiseMasterPage from "./components/FranchiseMasterPage";
import EventMonitorPage from "./components/EventMonitorPage";
import MasterSyncPage from "./components/MasterSyncPage";
import FranchiseStockTransferPage from "./components/FranchiseStockTransferPage";
import TenantSelectorPage from "./components/TenantSelectorPage";
import FranchiseTransferConfigPage from "./components/FranchiseTransferConfigPage";
import FranchiseMigrationPage from "./components/FranchiseMigrationPage";
import DbMigrationsPage from "./components/DbMigrationsPage";
import FranchiseUsersPage from "./components/FranchiseUsersPage";

// Accounting
import ReceiptEntry from "./components/accounting/ReceiptEntry";
import PaymentEntry from "./components/accounting/PaymentEntry";
import TrialBalance from "./components/accounting/TrialBalance";
import LedgerStatement from "./components/accounting/LedgerStatement";
import CustomerStatement from "./components/accounting/CustomerStatement";
import SupplierStatement from "./components/accounting/SupplierStatement";
import ProfitLoss from "./components/accounting/ProfitLoss";
import BalanceSheet from "./components/accounting/BalanceSheet";
import CashFlow from "./components/accounting/CashFlow";
import BankReconciliation from "./components/accounting/BankReconciliation";
import BankStatementImport from "./components/accounting/BankStatementImport";
import BankNarrationRulesPage from "./components/accounting/BankNarrationRulesPage";
import BankStatementReview from "./components/accounting/BankStatementReview";
import CashSummaryConfigPage from "./components/CashSummaryConfigPage";
import CashSummaryManualEntryPage from "./components/CashSummaryManualEntryPage";
import DailyCashSummaryReport from "./components/DailyCashSummaryReport";
import ExcessShortageReport from "./components/ExcessShortageReport";
import InventoryLedger from "./components/accounting/InventoryLedger";
import StockValuation from "./components/accounting/StockValuation";
import CustomerAging from "./components/accounting/CustomerAging";
import SupplierAging from "./components/accounting/SupplierAging";
import InterBranchTransfer from "./components/accounting/InterBranchTransfer";
import PeriodClosing from "./components/accounting/PeriodClosing";
import BudgetManager from "./components/accounting/BudgetManager";
import BudgetVsActual from "./components/accounting/BudgetVsActual";
import AccountingSetup from "./components/accounting/AccountingSetup";
import LedgerAccountsPage from "./components/accounting/LedgerAccountsPage";
import { UnitProvider } from "./components/UnitContext";
import { BranchProvider } from "./components/BranchContext";

// Lazily loaded: Product 360 pulls in React Flow v12 and the shared renderer. Everyone who
// never opens the page would otherwise pay for it on first load, and the main bundle is
// already over CRA's warning threshold.
const Product360Page = React.lazy(() => import("./features/product360/Product360Page"));
const InsightsPage = React.lazy(() => import("./features/insights/InsightsPage"));

// ========================
// ORDERED MENU ROUTE MAP  (mirrors Sidebar menuItems order)
// ========================
const ROUTE_ORDER = [
  { key: "Dashboard",                        path: "/dashboard" },
  { key: "AI Stock Intelligence",            path: "/ai-dashboard" },
  { key: "AI Report Assistant",             path: "/ai-report" },
  { key: "Admin Page",                       path: "/branch-request-list" },
  { key: "Connected POS Terminals",          path: "/pos-sessions" },
  { key: "Reprocess Voucher",               path: "/reprocess-voucher-form" },
  { key: "POS",                              path: "/pos" },
  { key: "KOT",                             path: "/kot" },
  { key: "Sales Entry",                      path: "/salesentryform" },
  { key: "HSN wise Sales",                   path: "/hsnsales" },
  { key: "HSN Sales Summary",                path: "/hsn-sales-summary" },
  { key: "HSN wise Purchase",                path: "/hsnwise-purchase-report" },
  { key: "Purchase Entry",                   path: "/purchaseentry" },
  { key: "Goods Receipt",                    path: "/goodsreceipt" },
  { key: "Production Def",                   path: "/production-def" },
  { key: "Production Planning",              path: "/production-planning" },
  { key: "Production Planning Import",       path: "/production-planning-import" },
  { key: "Production Execution",             path: "/production-execution" },
  { key: "Weighbridge",                      path: "/weighbridge" },
  { key: "Weight-Count",                     path: "/bridge-count" },
  { key: "WeighBridge Usage",                path: "/weighbridgeusage" },
  { key: "Weighbridge Resync",               path: "/weighbridge-resync" },
  { key: "Branch Details",                   path: "/branch-update" },
  { key: "POS Address Configuration",        path: "/pos-address-config" },
  { key: "Branch Day End Settings",          path: "/branch-day-end-settings" },
  { key: "Scheme Creation",                  path: "/schemepage" },
  { key: "Manage Scheme",                    path: "/publishschemepage" },
  { key: "Menu Master",                      path: "/menu-master" },
  { key: "Role Management",                  path: "/role-management" },
  { key: "Role Menu Access",                 path: "/role-menu" },
  { key: "Branch Creation",                  path: "/branchcreationpage" },
  { key: "User Creation",                    path: "/usercreationpage" },
  { key: "Branch Assignment",                path: "/branchassingment" },
  { key: "Transfer Branch Permissions",      path: "/branch-transfer-assignment" },
  { key: "Financial Year Setup",             path: "/financialyearpage" },
  { key: "Receipt Modes",                    path: "/receipt-modes" },
  { key: "UPI Payment Setup",               path: "/upi-config" },
  { key: "Item Search",                      path: "/itemsearch" },
  { key: "Item Creation",                    path: "/createitemmaster" },
  { key: "Branch Price",                     path: "/branch-price" },
  { key: "Stock Transfer Discount",          path: "/stock-transfer-discount" },
  { key: "Price Edit Category Wise",         path: "/category-price-edit" },
  { key: "Category Link",                    path: "/item-category-linker" },
  { key: "Category Type",                    path: "/categorytypemaster" },
  { key: "Category Name",                    path: "/categorynamemaster" },
  { key: "Supplier Creation",                path: "/suppliercreation" },
  { key: "Tax Update Manager",               path: "/tax-update-manager" },
  { key: "Tax Update Preview",               path: "/tax-update-preview" },
  { key: "Physical Stock Correction",        path: "/physical-stock-correction" },
  { key: "Wastage Entry",                    path: "/wastage-entry" },
  { key: "Wastage Report",                   path: "/wastage-report" },
  { key: "Sales Re Print",                   path: "/salessummaryreport" },
  { key: "Sales Report",                     path: "/sales" },
  { key: "Sales Tax Summary",                path: "/salestaxsummary" },
  { key: "Purchase Report",                  path: "/purchasereport" },
  { key: "Stock Movement Report",            path: "/stockmovementreport" },
  { key: "Item Movement Report",             path: "/item-movement-report" },
  { key: "Item Velocity Report",             path: "/item-velocity-report" },
  { key: "Physical Stock Report",            path: "/physicalstockreport" },
  { key: "All Branch Stock Report",          path: "/stock-report-all-branch" },
  { key: "Branch Stock Management",          path: "/branch-stock-report" },
  { key: "All Branch Sales Report",          path: "/sales-report-all-branch" },
  { key: "All Branch Categorywise Sales Report", path: "/sales-category-wise-report-all-branch" },
  { key: "Item Stock Report",                path: "/item-stock-report" },
  { key: "Day End Report",                   path: "/day-end-report" },
  { key: "Clear Day End",                    path: "/day-end-clear" },
  { key: "Bill Series Report",               path: "/billseriesreport" },
  { key: "Season Sales Report",              path: "/seasonalreport" },
  { key: "Stock Turnover Report",            path: "/stock-turnover" },
  { key: "Stock Anomaly Report",             path: "/stock-anomaly-report" },
  { key: "Report Exclusions",               path: "/report-exclusions" },
  { key: "Item Sales Report",                path: "/item-sales" },
  { key: "Documents List",                   path: "/documents-list" },
  { key: "Branch Stock Diff Report",         path: "/branch-stock-diff-report" },
  { key: "Branch Stock Report",              path: "/branch-stock-view" },
  { key: "Branch Inventory Report",          path: "/branch-inventory" },
  { key: "Branch Inventory Ledger",          path: "/branch-inventory-ledger" },
  { key: "Stock Transfer Out Report",        path: "/stocktransfer-out-report" },
  { key: "Stock Transfer In Report",         path: "/stocktransfer-in-report" },
  { key: "Production Planning Report",       path: "/production-planning-report" },
  { key: "Production Execution Report",      path: "/production-execution-report" },
  { key: "Raw Material Usage Report",        path: "/raw-material-usage-report" },
  { key: "Item Transfer Report",             path: "/item-transfer-report" },
  { key: "Salesman Report",                  path: "/salesman-report" },
  { key: "Category Item Report",             path: "/category-item-report" },
  { key: "Category Sales Summary Report",     path: "/category-sales-summary-report" },
  { key: "Branch Profit Report",             path: "/branch-profit-report" },
  { key: "Branch Monthly Expense",           path: "/branch-monthly-expense" },
  { key: "Monthly Branch Profit Report",     path: "/monthly-branch-profit-report" },
  { key: "Expense Head Management",          path: "/expense-head-management" },
  { key: "Item Cost Override",               path: "/item-cost-override" },
  { key: "Cost Price History",               path: "/cost-price-history" },
  { key: "Cost Stamping",                    path: "/cost-stamping" },
  // Franchise
  { key: "Franchise Master",                 path: "/franchise-master" },
  { key: "Event Monitor",                    path: "/event-monitor" },
  { key: "Master Sync",                      path: "/master-sync" },
  { key: "Franchise Stock Transfer",         path: "/franchise-stock-transfer" },
  { key: "Purchase Correction",             path: "/purchase-correction" },
  { key: "Purchase Correction Approval",    path: "/purchase-correction-approval" },
  { key: "Purchase Correction History",     path: "/purchase-correction-history" },
  // Accounting
  { key: "Accounting Setup",                 path: "/accounting/setup" },
  { key: "Ledger Accounts",                  path: "/accounting/ledger-accounts" },
  { key: "Receipt Entry",                   path: "/accounting/receipt-entry" },
  { key: "Payment Entry",                   path: "/accounting/payment-entry" },
  { key: "Trial Balance",                   path: "/accounting/trial-balance" },
  { key: "Ledger Statement",                path: "/accounting/ledger-statement" },
  { key: "Customer Statement",              path: "/accounting/customer-statement" },
  { key: "Supplier Statement",              path: "/accounting/supplier-statement" },
  { key: "Profit & Loss",                   path: "/accounting/profit-loss" },
  { key: "Balance Sheet",                   path: "/accounting/balance-sheet" },
  { key: "Cash Flow",                       path: "/accounting/cash-flow" },
  { key: "Bank Reconciliation",             path: "/accounting/bank-reconciliation" },
  { key: "Bank Statements",                 path: "/accounting/bank-statement-import" },
  { key: "Bank Narration Rules",            path: "/accounting/bank-narration-rules" },
  { key: "Bank Statement Review",           path: "/accounting/bank-statement-review" },
  { key: "Cash Summary Line Items",         path: "/accounting/cash-summary-line-items" },
  { key: "Cash Summary Manual Entries",     path: "/accounting/cash-summary-manual-entries" },
  { key: "Daily Cash Summary",              path: "/accounting/daily-cash-summary" },
  { key: "Inventory Ledger",                path: "/accounting/inventory-ledger" },
  { key: "Stock Valuation",                 path: "/accounting/stock-valuation" },
  { key: "Customer Aging",                  path: "/accounting/customer-aging" },
  { key: "Supplier Aging",                  path: "/accounting/supplier-aging" },
  { key: "Inter-Branch Transfer",           path: "/accounting/inter-branch-transfer" },
  { key: "Period Closing",                  path: "/accounting/period-closing" },
  { key: "Budget Manager",                  path: "/accounting/budget-manager" },
  { key: "Budget vs Actual",                path: "/accounting/budget-vs-actual" },
  { key: "Tally Integration",               path: "/accounting/tally" },
  { key: "Tally Sync Status",               path: "/accounting/tally-status" },
  { key: "Download",                         path: "/download" },
  { key: "Upload",                           path: "/uploadpage" },
  { key: "Invoice Designer",                 path: "/invoicedesigner" },
  { key: "Workflow Designer",                path: "/bpmn-editorr" },
  { key: "My Tasks",                         path: "/my-tasks" },
  { key: "My Reports",                       path: "/my-reports" },
  { key: "E-Invoicing",                      path: "/e-invoicing" },
  { key: "Languages",                        path: "/languages" },
  { key: "Workflow Instances",               path: "/workflow-instances" },
  { key: "About",                            path: "/about" },
  { key: "Help",                             path: "/help" },
];

/** Resolves the first route a user may land on after login. */
async function resolveLoginLanding(loginRoles) {
  // New tenants must complete the setup wizard before accessing the app
  const setupCompleted = localStorage.getItem("setupCompleted");
  if (setupCompleted === "false") return "/setup-wizard";

  if (loginRoles.includes("system-admin")) return "/dashboard";

  const tenancyId = localStorage.getItem("tenancyId");
  const token = localStorage.getItem("jwtToken");

  try {
    const res = await fetch(`/api/${tenancyId}/role-menus/accessible-menus`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify(loginRoles),
    });
    const data = res.ok ? await res.json() : [];
    const allowed = new Set(Array.isArray(data) ? data : []);

    // No role-menu assignments configured — default to dashboard
    if (allowed.size === 0) return "/dashboard";

    const first = ROUTE_ORDER.find(({ key }) => allowed.has(key));
    return first ? first.path : "/dashboard";
  } catch {
    return "/dashboard";
  }
}

// Redirects "/" to the first route the current user is permitted to access.
const LandingRedirect = ({ roles }) => {
  const navigate = useNavigate();
  useEffect(() => {
    resolveLoginLanding(roles).then((path) => navigate(path, { replace: true }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
};

// ========================
// AUTH WRAPPER COMPONENT
// ========================
const AuthenticatedApp = ({ mode, setMode, roles, setRoles }) => {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogin = async (loginRoles) => {
    localStorage.setItem("roles", JSON.stringify(loginRoles));
    setRoles(loginRoles);
    const landing = await resolveLoginLanding(loginRoles);
    navigate(landing);
  };

  const location = useLocation();
  const isAuthenticated = !!roles.length;
  const setupCompleted = localStorage.getItem("setupCompleted");
  const needsSetup = isAuthenticated && setupCompleted === "false";

  if (!isAuthenticated) {
    return <LoginForm onLogin={handleLogin} autoOpen={location.pathname === "/login"} />;
  }

  // Redirect to wizard if setup is not complete (but allow /setup-wizard itself)
  if (needsSetup && window.location.pathname !== "/setup-wizard") {
    return (
      <SetupWizardPage
        onComplete={() => {
          localStorage.setItem("setupCompleted", "true");
          navigate("/dashboard");
        }}
      />
    );
  }

  // Show wizard directly when on /setup-wizard route
  if (window.location.pathname === "/setup-wizard") {
    return (
      <SetupWizardPage
        onComplete={() => {
          localStorage.setItem("setupCompleted", "true");
          navigate("/dashboard");
        }}
      />
    );
  }

/**
 * Slides the top bar out of the way while reading down a page.
 *
 * The bar is position="fixed", so it costs 64px of viewport height on every screen
 * regardless of where you are on the page - that is the height complained about. Hiding it
 * on scroll-down returns those pixels to the content and brings the bar straight back on
 * scroll-up, so nothing has to be clicked to get it either way.
 *
 * Deliberately does NOT animate the content's margin-top. That margin only positions
 * content below the bar at scroll offset 0; shrinking it while scrolled would shift the
 * document under the user and change the scroll position, which can feed back into the
 * trigger and leave the bar flickering.
 */
function HideOnScroll({ children }) {
  // 80px rather than 0: below that, a trackpad's inertia or the browser's scroll
  // restoration can toggle the bar on a scroll the user did not intend to make.
  const scrolledDown = useScrollTrigger({ threshold: 80 });
  const [summoned, setSummoned] = useState(false);

  useEffect(() => {
    // GlobalMenuSearch binds Ctrl/Cmd+K to focus the search from anywhere. If the bar is
    // hidden when that fires, focus lands on an input translated off-screen - visibly
    // nothing happens. So the same shortcut has to bring the bar back first.
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") setSummoned(true);
      if (e.key === "Escape") setSummoned(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    // Once scrolled back up the bar is visible on its own, so drop the override - otherwise
    // one Ctrl+K would pin it open for the rest of the session.
    if (!scrolledDown) setSummoned(false);
  }, [scrolledDown]);

  return (
    <Slide appear={false} direction="down" in={!scrolledDown || summoned}>
      {children}
    </Slide>
  );
}

  return (
    <MenuAccessProvider roles={roles}>
      {/* Top app bar - carries the global menu search (AWS-console style).
          Hides on scroll-down, returns on scroll-up or Ctrl/Cmd+K. */}
      <HideOnScroll>
      <AppBar
        position="fixed"
        elevation={2}
        sx={{
          bgcolor: "#141a2e",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          width: { xs: "100%", sm: "calc(100% - 240px)" },
          ml: { xs: 0, sm: "240px" },
        }}
      >
        <Toolbar variant="dense" sx={{ minHeight: { xs: 48, sm: 64 } }}>
          <IconButton
            edge="start"
            onClick={() => setMobileOpen(true)}
            sx={{ mr: 1, color: "#ffe3a3", display: { xs: "inline-flex", sm: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 15,
              color: "#ffe3a3",
              letterSpacing: "0.3px",
              display: { xs: "block", sm: "none" },
              flexShrink: 0,
            }}
          >
            TradeLink 247
          </Typography>
          <GlobalMenuSearch />
        </Toolbar>
      </AppBar>
      </HideOnScroll>

      <BranchProvider>
      <UnitProvider>
      <Sidebar mode={mode} setMode={setMode} roles={roles} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <Box sx={{ display: "flex", flexGrow: 1, ml: { xs: 0, sm: "240px" }, mt: { xs: "48px", sm: 8 } }}>
        <WebSocketProvider>
          <Routes>
            <Route path="/" element={<LandingRedirect roles={roles} />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/sales" element={<SalesDetail />} />
            <Route path="/hsnsales" element={<HSNSalesDetail />} />
            <Route path="/hsn-sales-summary" element={<HsnSalesSummaryReport />} />
            <Route path="/purchasereport" element={<PurchaseDetail />} />
            <Route path="/weighbridge" element={<WeighBridge />} />
            <Route path="/branchcreationpage" element={<BranchCreationPage />} />
            <Route path="/branch-update" element={<BranchUpdatePage />} />
            <Route path="/pos-address-config" element={<PosAddressConfigPage />} />
            <Route path="/branch-day-end-settings" element={<BranchDayEndSettingsPage />} />
            <Route path="/usercreationpage" element={<UserCreationPage />} />
            <Route path="/schemepage" element={<SchemePage />} />
            <Route path="/publishschemepage" element={<PublishSchemePage />} />
            <Route path="/about" element={<About />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/download" element={<DownloadPage />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/seasonalreport" element={<SeasonalReport />} />
            <Route path="/categorytypemaster" element={<CategoryTypeMaster />} />
            <Route path="/categorynamemaster" element={<CategoryNameMaster />} />
            <Route path="/suppliercreation" element={<SupplierCreationForm />} />
            <Route path="/purchaseentry" element={<PurchaseEntryForm />} />
            <Route path="/salesentryform" element={<SalesEntryForm />} />
            <Route path="/billseriesreport" element={<BillSeriesReport />} />
            <Route path="/uploadpage" element={<UploadPage />} />
            <Route path="/createitemmaster" element={<CreateItemMaster />} />
            <Route path="/salessummaryreport" element={<SalesSummaryReport />} />
            <Route path="/stockmovementreport" element={<StockMovementReport />} />
            <Route path="/item-movement-report" element={<ItemMovementReport />} />
            <Route path="/item-velocity-report" element={<ItemVelocityReport />} />
            <Route path="/invoicedesigner" element={<Invoicedesigner />} />
            <Route path="/stock-turnover" element={<StockTurnoverReport />} />
            <Route path="/item-sales" element={<ItemSalesReport />} />
            <Route path="/documents-list" element={<DocumentList />} />
            <Route path="/hsnwise-purchase-report" element={<HSNWisePurchaseReport />} />
            <Route path="/item-stock-report" element={<StockReport />} />
            <Route path="/pos" element={<POSEntry />} />
            <Route path="/itemsearch" element={<ItemSearchPage />} />
            <Route path="/main" element={<MainLayout mode={mode} setMode={setMode} roles={roles} />} />

            <Route path="/stock-report-all-branch" element={<StockReportAllBranch />} />
            <Route path="/stock-anomaly-report" element={<StockAnomalyReport />} />
            <Route path="/report-exclusions" element={<StockReportExclusionPage />} />
            <Route path="/sales-report-all-branch" element={<SalesReportAllBranch />} />
            <Route path="/branch-sales-summary-report" element={<BranchSalesSummaryReport />} />
            <Route path="/bridge-count" element={<WeighBridgeEngageReport />} />
            <Route path="/weighbridgeusage" element={<WeighbridgeUsageReport />} />
            <Route path="/weighbridge-resync" element={<WeighBridgeResync />} />
            <Route path="/branch-request-list" element={<BranchRequestList />} />
            <Route path="/pos-sessions" element={<PosSessionsPage />} />
            <Route path="/reprocess-voucher-form" element={<ReprocessVoucherForm />} />
            <Route path="/branch-stock-report" element={<BranchStockReport />} />
            <Route path="/branch-stock-diff-report" element={<BranchStockDiffReport />} />
            <Route path="/item-category-linker" element={<ItemCategoryLinker />} />
            <Route path="/tax-update-manager" element={<TaxUpdateManager />} />
            <Route path="/tax-update-preview" element={<TaxUpdatePreview />} />
            <Route path="/sales-category-wise-report-all-branch" element={<SalesCategoryWiseReportAllBranch />} />
            <Route path="/bpmn-editorr" element={<RequireWorkflowMenuAccess><WorkflowDesignerPage /></RequireWorkflowMenuAccess>} />
            <Route path="/my-tasks" element={<RequireWorkflowMenuAccess menuKey="My Tasks"><MyTasksPage /></RequireWorkflowMenuAccess>} />
            <Route
              path="/insights"
              element={
                <RequireWorkflowMenuAccess menuKey="Insights">
                  <React.Suspense fallback={<Box sx={{ p: 3 }}><CircularProgress size={24} /></Box>}>
                    <InsightsPage />
                  </React.Suspense>
                </RequireWorkflowMenuAccess>
              }
            />
            <Route
              path="/product-360/:productId?"
              element={
                <RequireWorkflowMenuAccess menuKey="Product 360">
                  <React.Suspense fallback={<Box sx={{ p: 3 }}><CircularProgress size={24} /></Box>}>
                    <Product360Page />
                  </React.Suspense>
                </RequireWorkflowMenuAccess>
              }
            />
            <Route path="/workflow-instances" element={<RequireWorkflowMenuAccess menuKey="Workflow Instances"><WorkflowInstancesPage /></RequireWorkflowMenuAccess>} />
            <Route path="/stocktransfer-out-report" element={<StockTransferOutReport />} />
            <Route path="/stocktransfer-in-report" element={<StockTransferInReport />} />
            <Route path="/branchassingment" element={<BranchAssignment />} />
            <Route path="/branch-transfer-assignment" element={<BranchTransferAssignment />} />
            <Route path="/category-price-edit" element={<CategoryPriceEditDialog />} />
            <Route path="/kot" element={<KOTEntry />} />
              <Route path="/salestaxsummary" element={<SalesTaxSummary />} />
              <Route path="/physicalstockreport" element={<PhysicalStockEntryReport />} />
 <Route path="/financialyearpage" element={<FinancialYearPage />} />
            <Route path="/day-end-report" element={<DayEndReport />} />
            <Route path="/day-end-clear" element={<DayEndClearPage />} />
            <Route path="/branch-price" element={<BranchPricePage />} />
            <Route path="/stock-transfer-discount" element={<StockTransferDiscountPage />} />

<Route
  path="/stock-transfer-out/invoice/:voucherNumber"
  element={<StockTransferOutInvoice />}
/>

<Route
  path="/physical-stock-correction"
  element={<PhysicalStockCorrection />}
/>
<Route
  path="/wastage-entry"
  element={<RequireWorkflowMenuAccess menuKey="Wastage Entry"><WastageEntry /></RequireWorkflowMenuAccess>}
/>
<Route
  path="/wastage-report"
  element={<RequireWorkflowMenuAccess menuKey="Wastage Report"><WastageReport /></RequireWorkflowMenuAccess>}
/>
<Route path="/production-def" element={<ProductionDefPage />} />
<Route path="/production-planning" element={<ProductionPlanningPage />} />
<Route path="/production-planning-import" element={<ProductionPlanningImport />} />
<Route path="/production-execution" element={<ProductionExecutionPage />} />
<Route path="/production-planning-report" element={<ProductionPlanningReport />} />
<Route path="/production-execution-report" element={<ProductionExecutionReport />} />
<Route path="/raw-material-usage-report" element={<RawMaterialUsageReport />} />
<Route path="/goodsreceipt" element={<GoodsReceiptForm />} />
<Route path="/receipt-modes" element={<ReceiptModePage />} />
<Route path="/upi-config" element={<UpiConfigPage />} />
<Route path="/branch-stock-view" element={<BranchStockViewReport />} />
<Route path="/branch-inventory" element={<BranchInventoryReport />} />
<Route path="/branch-inventory-ledger" element={<BranchInventoryLedger />} />
<Route path="/item-transfer-report" element={<ItemTransferReport />} />
<Route path="/salesman-report" element={<SalesmanReport />} />
<Route path="/category-item-report" element={<CategoryItemReport />} />
<Route path="/category-sales-summary-report" element={<CategorySalesSummaryReport />} />
<Route path="/my-reports" element={<MyReports />} />
<Route path="/e-invoicing" element={<RegulatoryIntegration />} />
<Route path="/languages" element={<LanguagesPage />} />
<Route path="/e-invoicing/hmrc-callback" element={<TaxAuthorityCallback />} />
<Route path="/branch-profit-report" element={<BranchProfitReport />} />
<Route path="/branch-monthly-expense" element={<BranchExpenseEntryPage />} />
<Route path="/monthly-branch-profit-report" element={<MonthlyBranchProfitReport />} />
<Route path="/expense-head-management" element={<ExpenseHeadManagementPage />} />
<Route path="/shop-expense-report" element={<ShopExpenseConsolidatedReport />} />
<Route path="/item-cost-override" element={<ItemCostOverridePage />} />
<Route path="/cost-price-history" element={<CostPriceHistoryPage />} />
<Route path="/cost-stamping" element={<CostStampingPage />} />
<Route path="/purchase-correction" element={<PurchaseCorrectionPage />} />
<Route path="/purchase-correction-approval" element={<PurchaseCorrectionApprovalPage />} />
<Route path="/purchase-correction-history" element={<PurchaseCorrectionHistoryPage />} />
<Route path="/menu-map" element={<MenuMapPage />} />
<Route path="/ai-dashboard" element={<AIDashboardPage />} />
<Route path="/ai-report"    element={<AIReportChatbot />} />
<Route path="/menu-master" element={<MenuMasterPage />} />
<Route path="/role-menu" element={<RoleMenuPage />} />
<Route path="/role-management" element={<RoleManagementPage />} />
<Route path="/pos-machine-approval" element={<PosMachineApprovalPage />} />
<Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
<Route path="/refund-policy" element={<RefundPolicyPage />} />
<Route path="/privacy-policy" element={<PrivacyPolicyPage />} />

            {/* Accounting */}
            <Route path="/accounting/setup" element={<AccountingSetup />} />
            <Route path="/accounting/ledger-accounts" element={<LedgerAccountsPage />} />
            <Route path="/accounting/receipt-entry" element={<ReceiptEntry />} />
            <Route path="/accounting/payment-entry" element={<PaymentEntry />} />
            <Route path="/accounting/trial-balance" element={<TrialBalance />} />
            <Route path="/accounting/ledger-statement" element={<LedgerStatement />} />
            <Route path="/accounting/customer-statement" element={<CustomerStatement />} />
            <Route path="/accounting/supplier-statement" element={<SupplierStatement />} />
            <Route path="/accounting/profit-loss" element={<ProfitLoss />} />
            <Route path="/accounting/balance-sheet" element={<BalanceSheet />} />
            <Route path="/accounting/cash-flow" element={<CashFlow />} />
            <Route path="/accounting/bank-reconciliation" element={<BankReconciliation />} />
            <Route path="/accounting/bank-statement-import" element={<BankStatementImport />} />
            <Route path="/accounting/bank-narration-rules" element={<BankNarrationRulesPage />} />
            <Route path="/accounting/bank-statement-review" element={<BankStatementReview />} />
            <Route path="/accounting/cash-summary-line-items" element={<CashSummaryConfigPage />} />
            <Route path="/accounting/cash-summary-manual-entries" element={<CashSummaryManualEntryPage />} />
            <Route path="/accounting/daily-cash-summary" element={<DailyCashSummaryReport />} />
            <Route path="/accounting/excess-shortage" element={<ExcessShortageReport />} />
            <Route path="/accounting/inventory-ledger" element={<InventoryLedger />} />
            <Route path="/accounting/stock-valuation" element={<StockValuation />} />
            <Route path="/accounting/customer-aging" element={<CustomerAging />} />
            <Route path="/accounting/supplier-aging" element={<SupplierAging />} />
            <Route path="/accounting/inter-branch-transfer" element={<InterBranchTransfer />} />
            <Route path="/accounting/period-closing" element={<PeriodClosing />} />
            <Route path="/accounting/budget-manager" element={<BudgetManager />} />
            <Route path="/accounting/budget-vs-actual" element={<BudgetVsActual />} />
            <Route path="/accounting/tally" element={<TallyIntegrationPage />} />
            <Route path="/accounting/tally-status" element={<TallySyncStatusPage />} />

            {/* Franchise */}
            <Route path="/franchise-master"          element={<FranchiseMasterPage />} />
            <Route path="/event-monitor"             element={<EventMonitorPage />} />
            <Route path="/master-sync"               element={<MasterSyncPage />} />
            <Route path="/franchise-stock-transfer"  element={<FranchiseStockTransferPage />} />
            <Route path="/franchise-transfer-config" element={<FranchiseTransferConfigPage />} />
            <Route path="/franchise-migration"       element={<FranchiseMigrationPage />} />
            <Route path="/franchise-users"           element={<FranchiseUsersPage />} />
            <Route path="/db-migrations"             element={<DbMigrationsPage />} />

          </Routes>
        </WebSocketProvider>
      </Box>
      </UnitProvider>
      </BranchProvider>
    </MenuAccessProvider>
  );
};

// ========================
// ROOT APP
// ========================
const App = () => {
  const { i18n } = useTranslation();
  const [mode, setMode] = useState("dark");
  const [roles, setRoles] = useState(() => {
    try {
      if (window.location.pathname === "/login") return [];
      const token = localStorage.getItem("jwtToken");
      if (!token) return [];
      return JSON.parse(localStorage.getItem("roles") || "[]");
    } catch { return []; }
  });
  const [language, setLanguage] = useState("en");

  // Language preference
  useEffect(() => {
    const storedLanguage = localStorage.getItem("language");
    if (storedLanguage) {
      i18n.changeLanguage(storedLanguage);
      setLanguage(storedLanguage);
    } else if (localStorage.getItem("tenancyId") && localStorage.getItem("jwtToken")) {
      // Optional multi-language module: the tenant's default screen language for users who
      // haven't picked one. Off (the usual case) changes nothing.
      loadPrintPack(null).then((p) => {
        if (p.defaultUiLanguage && !localStorage.getItem("language")) {
          i18n.changeLanguage(p.defaultUiLanguage);
          setLanguage(p.defaultUiLanguage);
        }
      });
    }
  }, [i18n]);

  // Theme setup
  const theme = createTheme({
    typography: {
      fontSize: 12,
      h1: { fontSize: "2rem" },
      h2: { fontSize: "1.75rem" },
      h3: { fontSize: "1.5rem" },
    },
    palette: { mode },
  });

  return (
    <Suspense fallback={<CircularProgress />}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Routes>
            {/* Public pages — no auth required */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/partner" element={<PartnerPage />} />
            <Route path="/partner/apply" element={<PartnerApplyPage />} />
            <Route
              path="/signup"
              element={
                <SignUpPage
                  onLogin={async (loginRoles) => {
                    localStorage.setItem("roles", JSON.stringify(loginRoles));
                    setRoles(loginRoles);
                    const landing = await resolveLoginLanding(loginRoles);
                    window.location.href = landing;
                  }}
                />
              }
            />
            <Route path="/privacy" element={<PrivacyPolicyPage isPublic />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
            <Route path="/refund-policy" element={<RefundPolicyPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage isPublic />} />
            {/* Tenant selection — shown after login for multi-tenant users */}
            <Route
              path="/tenant-select"
              element={
                <TenantSelectorPage
                  onLogin={async (loginRoles) => {
                    localStorage.setItem("roles", JSON.stringify(loginRoles));
                    setRoles(loginRoles);
                    const landing = await resolveLoginLanding(loginRoles);
                    window.location.href = landing;
                  }}
                />
              }
            />
            {/* Authenticated app — all other routes */}
            <Route
              path="/*"
              element={
                <AuthenticatedApp
                  mode={mode}
                  setMode={setMode}
                  roles={roles}
                  setRoles={setRoles}
                />
              }
            />
          </Routes>
        </Router>
      </ThemeProvider>
    </Suspense>
  );
};

export default App;
